import React from 'react';
import PropTypes from 'prop-types';

import DataGrid from '../../Components/DataGrid';
import {
  getEltAndAccessor,
  getEltAndAccessorDate,
} from './common/helpers';
import DateFilterElt from './common/DateFilterElt';
import HeaderElt from './common/HeaderElt';
import HostFilterElt from './common/HostFilterElt';

import './common/ResourcesTable.scss';

/*
Constants and Helpers
*/

// The list of the table's filter keys
export const TABLE_FILTERS = [
  'name',
  'created',
  'firstSeen',
  'lastSeen',
];

export const getRows = ({
  data,
  onClickElt = () => () => {},
}) => {
  const rows = data
    .map((elt) => {
      const {
        created,
        firstSeen,
        id: hostId,
        lastSeen,
        name,
      } = elt;

      // Name
      const {
        accessor: nameAccessor,
        element: nameElt,
      } = getEltAndAccessor({
        clickable: !!hostId,
        label: name,
        onClickElt: onClickElt(hostId),
      });

      // Created
      const {
        accessor: createdAccessor,
        element: createdElt,
        label: createdLabel,
      } = getEltAndAccessorDate(created);

      // First Seen
      const {
        accessor: firstSeenAccessor,
        element: firstSeenElt,
        label: firstSeenLabel,
      } = getEltAndAccessorDate(firstSeen);

      // Last Seen
      const {
        accessor: lastSeenAccessor,
        element: lastSeenElt,
        label: lastSeenLabel,
      } = getEltAndAccessorDate(lastSeen);

      return ({
        name: <strong>{nameElt}</strong>,
        nameAccessor,

        created: createdElt,
        createdAccessor,
        createdLabel,

        firstSeen: firstSeenElt,
        firstSeenAccessor,
        firstSeenLabel,

        lastSeen: lastSeenElt,
        lastSeenAccessor,
        lastSeenLabel,
      });
    });

  return rows;
};

const getColumns = ({
  filters = {},
  setFilter,
  setSorting,
  sorting,
}) => {
  const {
    createdOption,
    firstSeenOption,
    lastSeenOption,
    nameValue,
  } = filters;

  return [
    {
      Header: (
        <HeaderElt
          fieldName="name"
          label="Name"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: 'nameAccessor',
      filterable: true,
      sortable: false,
      Cell: row => row.original.name,
      maxWidth: 420,
      minWidth: 320,
      Filter: (
        <HostFilterElt
          fieldName="name"
          filterValue={nameValue}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="firstSeen"
          label="First Seen"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: 'firstSeenAccessor',
      filterable: true,
      sortable: false,
      Cell: row => row.original.firstSeen,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="firstSeen"
          optionKey={firstSeenOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="created"
          label="Tracked Since"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: 'createdAccessor',
      filterable: true,
      sortable: false,
      Cell: row => row.original.created,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="created"
          optionKey={createdOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="lastSeen"
          label="Last Seen"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: 'lastSeenAccessor',
      filterable: true,
      sortable: false,
      Cell: row => row.original.lastSeen,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="lastSeen"
          optionKey={lastSeenOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },
  ];
};

/*
Component
*/

const HostnamesTable = (props) => {
  const {
    data,
    filters,
    pageSize,
    setFilter,
    sorting = {},
    setSorting = () => {},
  } = props;

  const columns = getColumns({
    sorting,
    filters,
    setFilter,
    setSorting,
  });

  return (
    <div className="resources-table">
      <DataGrid
        data={data}
        pageSize={pageSize}
        columns={columns}
        showPagination={false}
      />
    </div>
  );
};

HostnamesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  filters: PropTypes.shape({}),
  pageSize: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  setFilter: PropTypes.func,
  setSorting: PropTypes.func,
  sorting: PropTypes.shape({}),
};

HostnamesTable.defaultProps = {
  filters: {},
  data: [],
  pageSize: 25,
  setFilter: () => {},
  setSorting: () => {},
  sorting: {},
};

export default HostnamesTable;
